/**
 * The exercise bank: everything the app can ask a student to do.
 *
 * Two sources feed it — authored exercises (a specific problem an author wants
 * every student to meet) and templates (an endless supply of the same shape).
 * Callers ask for "eight problems on adding fractions, around difficulty 3" and
 * never care which source answered.
 */

import { hashString } from '@yuan-math/math';
import { clampDifficulty, fingerprint, generateExercise, type ExerciseTemplate } from './generator.ts';
import type { Difficulty, Exercise } from './types.ts';

export type PracticeRequest = {
  conceptId: string;
  count: number;
  /** Centre of the difficulty band. Omit to sweep from easy to hard. */
  difficulty?: Difficulty;
  /** Anything stable per session — the attempt id, the date, the student id. */
  seed?: string;
};

export class ExerciseBank {
  private readonly templatesByConcept = new Map<string, ExerciseTemplate[]>();
  private readonly staticByConcept = new Map<string, Exercise[]>();
  private readonly templatesById = new Map<string, ExerciseTemplate>();

  constructor(templates: readonly ExerciseTemplate[] = [], authored: readonly Exercise[] = []) {
    this.registerTemplates(templates);
    this.registerExercises(authored);
  }

  registerTemplates(templates: readonly ExerciseTemplate[]): void {
    for (const template of templates) {
      if (this.templatesById.has(template.id)) throw new Error(`Duplicate template id: ${template.id}`);
      if (template.difficulties.length === 0) throw new Error(`Template ${template.id} declares no difficulties`);
      this.templatesById.set(template.id, template);
      const bucket = this.templatesByConcept.get(template.conceptId) ?? [];
      bucket.push(template);
      this.templatesByConcept.set(template.conceptId, bucket);
    }
  }

  registerExercises(exercises: readonly Exercise[]): void {
    for (const exercise of exercises) {
      const bucket = this.staticByConcept.get(exercise.conceptId) ?? [];
      bucket.push(exercise);
      this.staticByConcept.set(exercise.conceptId, bucket);
    }
  }

  templatesFor(conceptId: string): ExerciseTemplate[] {
    return this.templatesByConcept.get(conceptId) ?? [];
  }

  authoredFor(conceptId: string): Exercise[] {
    return this.staticByConcept.get(conceptId) ?? [];
  }

  hasContent(conceptId: string): boolean {
    return this.templatesFor(conceptId).length > 0 || this.authoredFor(conceptId).length > 0;
  }

  conceptsWithContent(): string[] {
    return [...new Set([...this.templatesByConcept.keys(), ...this.staticByConcept.keys()])].sort();
  }

  /**
   * Builds a practice set.
   *
   * Without an explicit difficulty the set ramps: the first problems sit at the
   * easiest difficulty the concept offers and the last at the hardest, so a
   * student always starts on something they can do.
   */
  practiceSet(request: PracticeRequest): Exercise[] {
    const { conceptId, count } = request;
    const templates = this.templatesFor(conceptId);
    const authored = this.authoredFor(conceptId);
    if (templates.length === 0 && authored.length === 0) return [];

    const seed = request.seed ?? 'default';
    const out: Exercise[] = [];
    const seen = new Set<string>();

    const take = (exercise: Exercise): void => {
      const print = fingerprint(exercise);
      if (seen.has(print)) return;
      seen.add(print);
      out.push(exercise);
    };

    // Authored exercises come first: they exist because someone chose them.
    const wanted = request.difficulty;
    for (const exercise of authored) {
      if (out.length >= count) break;
      if (wanted !== undefined && Math.abs(exercise.difficulty - wanted) > 1) continue;
      take(exercise);
    }

    if (templates.length === 0) return out.slice(0, count);

    const available = [...new Set(templates.flatMap((t) => t.difficulties))].sort((a, b) => a - b);
    const ladder = buildLadder(available, count, wanted);

    /*
     * Bounded retries: a template can legitimately repeat itself on a small
     * parameter space, and we would rather return fewer items than spin.
     *
     * `index` is the rung of the difficulty ladder, and it climbs when an
     * exercise is accepted. It must *also* climb when a rung is exhausted —
     * a template with only a handful of distinct problems at difficulty 2
     * otherwise pins the ladder there and starves the concept of everything
     * above it. That was a real bug: `exponents` returned four problems for a
     * request of thirty, because one narrow template held the bottom rung.
     */
    let attempt = 0;
    let index = out.length;
    let stalled = 0;

    while (out.length < count && attempt < count * 6) {
      const difficulty = ladder[Math.min(ladder.length - 1, index)] ?? available[0] ?? 1;
      const candidates = templates.filter((t) => t.difficulties.includes(difficulty as Difficulty));
      const pool = candidates.length > 0 ? candidates : templates;
      const template = pool[(hashString(`${seed}:${attempt}`) + index) % pool.length] as ExerciseTemplate;
      const supported = template.difficulties.includes(difficulty as Difficulty)
        ? (difficulty as Difficulty)
        : (template.difficulties[0] as Difficulty);
      const before = out.length;
      take(generateExercise(template, supported, `${seed}:${attempt}`));
      attempt += 1;

      if (out.length > before) {
        index += 1;
        stalled = 0;
        continue;
      }

      // Every template on this rung has been asked, and none had anything new
      // to give. Move up rather than asking them all again.
      stalled += 1;
      if (stalled >= pool.length * 2) {
        index += 1;
        stalled = 0;
      }
    }

    return out.slice(0, count);
  }

  /** Regenerates the exact same problem — used to restore an in-flight session. */
  rebuild(templateId: string, difficulty: Difficulty, seed: string): Exercise | undefined {
    const template = this.templatesById.get(templateId);
    return template ? generateExercise(template, difficulty, seed) : undefined;
  }

  /** One problem at a target difficulty, for adaptive drills and diagnostics. */
  single(conceptId: string, difficulty: number, seed: string): Exercise | undefined {
    const target = clampDifficulty(difficulty);
    const templates = this.templatesFor(conceptId);
    if (templates.length > 0) {
      const exact = templates.filter((t) => t.difficulties.includes(target));
      const pool = exact.length > 0 ? exact : templates;
      const template = pool[hashString(seed) % pool.length] as ExerciseTemplate;
      const supported = template.difficulties.includes(target)
        ? target
        : (closest(template.difficulties, target) as Difficulty);
      return generateExercise(template, supported, seed);
    }
    const authored = this.authoredFor(conceptId);
    if (authored.length === 0) return undefined;
    const sorted = [...authored].sort(
      (a, b) => Math.abs(a.difficulty - target) - Math.abs(b.difficulty - target),
    );
    return sorted[0];
  }
}

function closest(values: readonly Difficulty[], target: number): Difficulty {
  return [...values].sort((a, b) => Math.abs(a - target) - Math.abs(b - target))[0] as Difficulty;
}

/** Difficulty for each position in the set: a ramp, or a band around `wanted`. */
function buildLadder(available: number[], count: number, wanted?: Difficulty): number[] {
  if (available.length === 0) return Array.from({ length: count }, () => 1);
  if (wanted !== undefined) {
    const near = available.filter((d) => Math.abs(d - wanted) <= 1);
    const pool = near.length > 0 ? near : [closest(available as Difficulty[], wanted)];
    return Array.from({ length: count }, (_, i) => pool[i % pool.length] as number);
  }
  return Array.from({ length: count }, (_, i) => {
    const position = count <= 1 ? 0 : i / (count - 1);
    const index = Math.round(position * (available.length - 1));
    return available[index] as number;
  });
}
